<template>
  <v-dialog
    v-model="localDialog"
    width="60%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6 bg-blue-grey-lighten-4">
        <span>{{ isEntryMode ? "Add Exchange Rate" : "Update Exchange Rate" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="LcExchangeRateSetup">
          <v-row>
            <v-col lg="6" md="6" cols="12">
              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="currency_id.value.value"
                :error-messages="currency_id.errorMessage.value"
                clearable
                :items="getCurrencyList"
                item-title="name"
                item-value="id"
                :menu-props="{ width: 300 }"
              >
                <template #label>
                  Currency <span class="text-red-accent-4">*</span>
                </template>
                <template v-slot:selection="{ item }">
                  <span v-if="item">{{ item.raw.code }} {{ item.raw.symbol }}</span>
                </template>
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props" v-slot="" :title="null">
                    <div class="d-flex align-center" style="width: 100%;">
                      <span class="mr-3 text-h6 font-weight-bold">{{ item.raw.symbol }}</span>
                      <div class="flex-grow-1">
                        <span class="font-weight-medium">{{ item.raw.name }}</span>
                        <small class="text-grey ml-2">({{ item.raw.code }})</small>
                      </div>
                    </div>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <v-text-field
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="rate.value.value"
                :error-messages="rate.errorMessage.value"
                clearable
                type="number"
                step="0.0001"
              >
                <template #label>
                  Exchange Rate <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-select
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="type.value.value"
                :error-messages="type.errorMessage.value"
                :items="[{text: 'Import', value: 'import'}, {text: 'Export', value: 'export'}]"
                item-title="text"
                item-value="value"
                clearable
              >
                <template #label>
                  Type <span class="text-red-accent-4">*</span>
                </template>
              </v-select>
            </v-col>

            <v-col lg="6" md="6" cols="12">
              <v-date-input
                v-model="rate_date.value.value"
                label="Rate Date *"
                variant="outlined"
                density="compact"
                bg-color="grey-lighten-4"
                :error-messages="rate_date.errorMessage.value"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
              ></v-date-input>

              <v-textarea
                label="Remarks (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
                auto-grow
                rows="2"
              ></v-textarea>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                hide-details
                color="primary"
                true-value="1"
                false-value="0"
              ></v-checkbox>
            </v-col>

            <input v-model="exchange_rate_id.value.value" type="hidden" />
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  name: "LcExchangeRateSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        currency_id: "",
        rate: "",
        type: "",
        rate_date: "",
        remarks: "",
        status: "1",
        exchange_rate_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      isEntryMode: true,
    };
  },
  computed: {
    getCurrencyList() {
      // Get currency list from store
      const currencies = this.$store.getters.getCurrencyList || [];
      return currencies;
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
      if (val) {
        // Load currencies when dialog opens
        if (this.$store.getters.getCurrencyList.length == 0) {
          this.$store.dispatch('getCurrencyList');
        }
      }
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.currency_id.setValue(newData.currency_id || "", false);
        this.rate.setValue(newData.rate || "", false);
        this.type.setValue(newData.type || "", false);
        this.rate_date.setValue(newData.rate_date ? new Date(newData.rate_date) : "", false);
        this.remarks.setValue(newData.remarks || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.exchange_rate_id.setValue(newData.id || "", false);
      },
    },
  },
  created() {
    // Load currency list if not already loaded
    if (this.$store.getters.getCurrencyList.length == 0) {
      this.$store.dispatch('getCurrencyList');
    }
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        currency_id: yup.string().required().label("Currency"),
        rate: yup.number().required().positive().min(0.0001).label("Exchange Rate"),
        type: yup.string().required().oneOf(['import', 'export']).label("Type"),
        rate_date: yup.date().required().label("Rate Date"),
        status: yup.string().required().label("Status"),
        remarks: yup.string().max(255).nullable().trim().label("Remarks"),
      }),
    });

    const currency_id = useField("currency_id");
    const rate = useField("rate");
    const type = useField("type");
    const rate_date = useField("rate_date");
    const remarks = useField("remarks");
    const status = useField("status");
    const exchange_rate_id = useField("exchange_rate_id");
    const store = useStore();

    const submit = handleSubmit((values) => {
      store
        .dispatch("saveUpdateLcExchangeRate", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          store.dispatch("getLcExchangeRateList");
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to save");
        });
    });
    
    return {
      submit,
      currency_id,
      rate,
      type,
      rate_date,
      remarks,
      status,
      exchange_rate_id,
      resetForm,
    };
  },
};
</script>

<style scoped>
.bg-brand {
  background-color: #1976d2;
  color: white;
}
</style>

